import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { Modal } from '../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../shared/components/modal/modal-footer.directive';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Select, SelectOption } from '../../../shared/components/forms/select';
import { Toggle } from '../../../shared/components/forms/toggle';
import { ToastService } from '../../../shared/services/toast-service';
import { LanguageService } from '../../../core/i18n/language.service';
import { AlertRuleService } from '../services/alert-rule.service';
import { AlertRule, AlertRuleOptions, AlertRuleRequest, optionLabel } from '../models/alert.model';

/**
 * Rule builder ("Add rule" / "Edit rule") — the stacked-form modal from the
 * alerts design (option 1d). Owns the create/update call so the duplicate-name
 * 409 can be surfaced verbatim on the name field; the parent just refetches on
 * `saved`. Metric/operator dropdowns are fed exclusively from /options — free
 * text would earn us the framework-shaped 400.
 */
@Component({
  selector: 'app-rule-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    Modal,
    ModalFooterDirective,
    FormField,
    TextInput,
    NumberInput,
    Select,
    Toggle,
  ],
  templateUrl: './rule-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RuleDialogComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly ruleService = inject(AlertRuleService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);

  /** Vocabulary from GET /alert-rule/options, loaded by the parent. */
  readonly options = input.required<AlertRuleOptions>();
  /** Rule to edit, or null to create. */
  readonly rule = input<AlertRule | null>(null);

  readonly closed = output<void>();
  /** Emitted after a successful create/update, before the modal closes. */
  readonly saved = output<void>();

  protected readonly open = signal(true);
  protected readonly busy = signal(false);
  /** Server-side name error (the 409 message, verbatim). */
  protected readonly nameError = signal<string | null>(null);

  protected readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    metric: ['', Validators.required],
    operator: ['', Validators.required],
    threshold: this.fb.control<number | null>(null, Validators.required),
    enabled: [true],
  });

  private readonly selectedMetric = toSignal(this.form.controls.metric.valueChanges, {
    initialValue: this.form.controls.metric.value,
  });

  // Option labels are translations keyed by enum value (alerts.options.*) —
  // the computeds read languageService.current() to re-evaluate on switch.
  protected readonly metricOptions = computed<SelectOption[]>(() => {
    this.languageService.current();
    return this.options().metrics.map((m) => {
      const label = optionLabel((k) => this.transloco.translate(k), 'metrics', m.value);
      return { value: m.value, label: m.unit ? `${label} (${m.unit})` : label };
    });
  });

  protected readonly operatorOptions = computed<SelectOption[]>(() => {
    this.languageService.current();
    return this.options().operators.map((o) => ({
      value: o.value,
      label: optionLabel((k) => this.transloco.translate(k), 'operators', o.value),
    }));
  });

  /** Unit suffix on the threshold input — from the API vocabulary, not hardcoded. */
  protected readonly thresholdUnit = computed(
    () => this.options().metrics.find((m) => m.value === this.selectedMetric())?.unit ?? '',
  );

  /**
   * Condition-less metrics (thresholdBased: false, i.e. OFFLINE) carry no
   * operator/threshold — a static hint replaces the inputs.
   */
  protected readonly isConditionless = computed(
    () =>
      this.options().metrics.find((m) => m.value === this.selectedMetric())?.thresholdBased ===
      false,
  );

  ngOnInit(): void {
    const rule = this.rule();
    if (rule) {
      this.form.patchValue({
        name: rule.name,
        metric: rule.metric,
        operator: rule.operator ?? '',
        threshold: rule.threshold,
        enabled: rule.enabled,
      });
    }

    // Condition-less metrics have no condition: disabling the controls takes
    // them (and their `required` validators) out of the form's validity.
    this.form.controls.metric.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((metric) => this.syncConditionControls(metric));
    this.syncConditionControls(this.form.controls.metric.value);

    // A new name invalidates the previous duplicate-name error.
    this.form.controls.name.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.nameError.set(null));
  }

  protected onClose(): void {
    this.closed.emit();
  }

  protected onSubmit(): void {
    if (this.busy()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const conditionless = this.isConditionless();
    const request: AlertRuleRequest = {
      name: value.name.trim(),
      metric: value.metric,
      operator: conditionless ? null : value.operator,
      threshold: conditionless ? null : value.threshold!,
      enabled: value.enabled,
    };

    this.busy.set(true);
    const existing = this.rule();
    const call = existing
      ? this.ruleService.updateRule(existing.id, request)
      : this.ruleService.createRule(request);

    call.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show(
          this.transloco.translate(existing ? 'alerts.rules.toast.updated' : 'alerts.rules.toast.created'),
          'success',
        );
        this.saved.emit();
        this.open.set(false);
      },
      error: (err) => {
        this.busy.set(false);
        if (err?.status === 409) {
          // The duplicate-name message is worth surfacing verbatim.
          this.nameError.set(
            err.error?.message || this.transloco.translate('alerts.rules.toast.duplicateName'),
          );
        } else {
          this.toastService.show(this.transloco.translate('alerts.rules.toast.saveFailed'), 'error');
        }
      },
    });
  }

  private syncConditionControls(metric: string): void {
    const { operator, threshold } = this.form.controls;
    const conditionless =
      this.options().metrics.find((m) => m.value === metric)?.thresholdBased === false;
    if (conditionless) {
      operator.disable();
      threshold.disable();
    } else {
      operator.enable();
      threshold.enable();
    }
  }
}
